<script setup>
import { computed } from 'vue'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'

defineOptions({ layout: PublicLayout })

const page = usePage()
const inquirePath = computed(() => page.props.inquire_path || '/inquire')
const channels = computed(() => page.props.brand?.channels || {})
const messageChannels = computed(() => channels.value.message || [])

function platformLabel(p) { return p.charAt(0).toUpperCase() + p.slice(1) }

const form = useForm({ name: '', email: '', phone: '', message: '', topic: 'contact', website: '' })

function submit() {
  form
    .transform((data) => ({ inquiry: data }))
    .post(inquirePath.value, { preserveScroll: true })
}
</script>

<template>
  <Head>
    <title>Contact · Kenya2Go</title>
    <meta name="description" content="Talk to a real person about your Kenya trip — by form, WhatsApp or Telegram.">
  </Head>

  <div class="kp">
    <header class="kp-hero">
      <div class="kp-hero__inner">
        <p class="kp-eyebrow">Contact</p>
        <h1 class="kp-hero__title">Talk to a real person</h1>
        <p class="kp-hero__lede">Tell us what you’re dreaming up and we’ll help shape it — no obligation, no bots.</p>
      </div>
    </header>

    <section class="kp-section clayout">
      <form class="cform" @submit.prevent="submit">
        <div class="cfield">
          <label for="c-name">Your name</label>
          <input id="c-name" v-model="form.name" type="text" autocomplete="name">
        </div>
        <div class="crow">
          <div class="cfield">
            <label for="c-email">Email</label>
            <input id="c-email" v-model="form.email" type="email" autocomplete="email" placeholder="you@example.com">
          </div>
          <div class="cfield">
            <label for="c-phone">Phone / WhatsApp</label>
            <input id="c-phone" v-model="form.phone" type="tel" autocomplete="tel" placeholder="+254…">
          </div>
        </div>
        <div class="cfield">
          <label for="c-msg">What are you thinking about?</label>
          <textarea id="c-msg" v-model="form.message" rows="5" placeholder="Dates, group size, what you’d love to see…"></textarea>
        </div>
        <input v-model="form.website" type="text" tabindex="-1" autocomplete="off" class="hp" aria-hidden="true">
        <button type="submit" class="kp-btn kp-btn--solid cform__submit" :disabled="form.processing">
          {{ form.processing ? 'Sending…' : 'Send request' }}
        </button>
        <p class="cform__note">We’ll only use your details to help with your trip. A human replies, usually within a day.</p>
      </form>

      <aside class="cside">
        <div class="cbox">
          <h3 class="cbox__h">Prefer to chat?</h3>
          <p class="cbox__p">Message us directly — we’re quick on WhatsApp and Telegram.</p>
          <div v-if="messageChannels.length" class="cchannels">
            <a v-for="c in messageChannels" :key="c.id" :href="c.url" target="_blank" rel="noopener" class="cchannel">
              {{ platformLabel(c.platform) }}
            </a>
          </div>
          <p v-else class="cbox__soon">Chat channels are being set up — use the form for now.</p>
        </div>
        <div class="cbox">
          <h3 class="cbox__h">Good to know</h3>
          <ul class="cbox__list">
            <li>Based in Kenya · East Africa Time (GMT+3)</li>
            <li>Experiences run by local, vetted operators</li>
            <li>Request first — pay once it’s confirmed</li>
          </ul>
        </div>
      </aside>
    </section>
  </div>
</template>

<style scoped>
.clayout { display: grid; grid-template-columns: 1.4fr 1fr; gap: 3rem; align-items: start; }
.cform { display: flex; flex-direction: column; gap: 1.1rem; }
.crow { display: grid; grid-template-columns: 1fr 1fr; gap: 1.1rem; }
.cfield { display: flex; flex-direction: column; gap: 0.4rem; }
.cfield label { font-size: 0.85rem; font-weight: 600; color: var(--ink); }
.cfield input, .cfield textarea { padding: 0.75rem 0.9rem; border: 1px solid var(--lineStrong); background: var(--surface); color: var(--ink); font: inherit; font-size: 0.95rem; }
.cfield textarea { resize: vertical; }
.hp { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
.cform__submit { align-self: flex-start; margin-top: 0.4rem; }
.cform__note { font-size: 0.82rem; color: var(--muted); margin: 0; }

.cside { display: flex; flex-direction: column; gap: 1.2rem; }
.cbox { padding: 1.4rem 1.5rem; border-radius: var(--radius-lg); background: var(--surface); border: 1px solid var(--line); }
.cbox__h { font-family: var(--display); font-weight: 500; font-size: 1.2rem; margin: 0 0 0.5rem; color: var(--ink); }
.cbox__p { font-size: 0.94rem; line-height: 1.55; color: var(--inkSoft); margin: 0 0 0.9rem; }
.cbox__soon { font-size: 0.88rem; color: var(--muted); margin: 0; }
.cchannels { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.cchannel { padding: 0.5rem 1rem; background: var(--accentSoft); color: var(--accent); border-radius: 999px; font-weight: 600; font-size: 0.88rem; text-decoration: none; }
.cbox__list { margin: 0; padding-left: 1.1rem; color: var(--inkSoft); line-height: 1.8; font-size: 0.92rem; }

@media (max-width: 760px) { .clayout { grid-template-columns: 1fr; gap: 2rem; } .crow { grid-template-columns: 1fr; } }
</style>
